/**
 * RTK Query Error Notification Middleware
 *
 * Automatically dispatches notifications for RTK Query errors, providing
 * consistent error handling across all API calls.
 *
 * @module notificationMiddleware
 */

import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';
import { addNotification } from '../slices/notificationSlice';
import { notificationMessages } from '../constants/notificationMessages';
import type { NotificationInput } from '../types/notification.types';
import { getUnauthorizedHandler } from './unauthorizedHandler';

/**
 * Shape of RTK Query rejected action payload.
 * Contains HTTP status and optional error data from the API response.
 */
interface RejectedPayload {
  status?: number | string;
  data?: {
    message?: string;
    error?: string;
  };
}

/**
 * Middleware that intercepts RTK Query rejected actions and dispatches
 * appropriate notifications based on HTTP status codes.
 *
 * Features:
 * - Maps HTTP status codes to pre-configured notification messages
 * - Extracts custom error messages from API responses when available
 * - Handles network errors (no status code) with connection error notification
 * - Excludes getCurrentUser 401 responses (expected behavior during auth checks)
 *
 * @example
 * ```typescript
 * import { rtkQueryErrorMiddleware } from '@loci/store';
 *
 * const store = configureStore({
 *   middleware: (getDefaultMiddleware) =>
 *     getDefaultMiddleware()
 *       .concat(api.middleware)
 *       .concat(rtkQueryErrorMiddleware),
 * });
 * ```
 */
export const rtkQueryErrorMiddleware: Middleware = (api) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    const payload = action.payload as RejectedPayload;
    const status =
      typeof payload?.status === 'string' ? parseInt(payload.status, 10) : payload?.status;
    const data = payload?.data;

    // Skip notifications for getCurrentUser — it's an auth probe, not a user-visible
    // request. A 401 is the normal "not logged in" state, and the endpoint's
    // transformErrorResponse rewrites the payload shape so the original status
    // isn't visible here anyway. Drop any rejected getCurrentUser action regardless of status.
    //
    // Endpoint detection: prefer RTK Query's `meta.arg.endpointName` when available.
    // Fall back to extracting the endpoint name from the action type, which has the
    // shape `<api>/<query|mutation>/<endpointName>/<status>` (e.g.
    // `lociApi/executeQuery/getCurrentUser/rejected`). Using `split('/')[2]`
    // for the fallback is exact — a previous `actionType.includes('getCurrentUser')`
    // would also have suppressed any future endpoint with `getCurrentUser` as a
    // substring (e.g. `getCurrentUserProfile`).
    const endpointName = (action as { meta?: { arg?: { endpointName?: string } } }).meta?.arg
      ?.endpointName;
    const actionType = (action as { type?: string })?.type || '';
    const endpointNameFromType = actionType.split('/')[2];
    const isGetCurrentUser =
      endpointName === 'getCurrentUser' || endpointNameFromType === 'getCurrentUser';
    if (isGetCurrentUser) {
      return next(action);
    }

    // Suppress all `passkeyAuthenticationBegin` failures from the toast surface.
    //
    // `usePasskeyAuthentication` fires this mutation once on /login mount as a
    // capability probe — the user never initiates it. The hook owns the failure
    // path (sets `isSupported=false` and logs via console.warn). Any toast here
    // is noise that misrepresents a feature-detection failure as a user-facing
    // error. Previously seen benign failure modes for this endpoint:
    //
    //   - 503 `PASSKEY_DISABLED` — backend env vars unset (dev / preview).
    //   - 429 — auth rate limit exhausted by repeated mounts (HMR, StrictMode).
    //   - 403 `CSRF_TOKEN_MISSING` — probe and `GET /csrf-token` race on mount,
    //     probe wins → reads cookie before it's set. Surfaced a scary
    //     "Missing CSRF token" toast on every prod /login page load.
    //
    // Match is exact on the endpoint name so an endpoint with a substring-
    // matching name cannot accidentally claim the suppression. See FRE-789
    // for the probe contract and docs/auth-issues-2026-04-20.md (Issue 6).
    const isPasskeyAuthenticationBegin =
      endpointName === 'passkeyAuthenticationBegin' ||
      endpointNameFromType === 'passkeyAuthenticationBegin';
    if (isPasskeyAuthenticationBegin) {
      return next(action);
    }

    let notification: NotificationInput;

    // status === 0 typically indicates network failure (CORS, offline, etc.)
    if (status === undefined || status === null || Number.isNaN(status) || status === 0) {
      notification = notificationMessages.network.CONNECTION_ERROR;
    } else if (status >= 500) {
      notification = notificationMessages.network.SERVER_ERROR;
    } else if (status === 429) {
      notification = notificationMessages.network.RATE_LIMIT;
    } else if (status === 408) {
      notification = notificationMessages.network.REQUEST_TIMEOUT;
    } else if (status === 401) {
      // Give the app's registered handler first refusal. On the web app this
      // funnels a stale-session 401 into `sanitizeInvalidSession()` (clear hint,
      // evict cookies, redirect to /login?force=1). When it takes ownership we
      // suppress the generic toast — the redirect is the user-facing signal.
      const resolvedEndpoint = endpointName ?? endpointNameFromType;
      const handled =
        getUnauthorizedHandler()?.({ status, endpointName: resolvedEndpoint }) === true;
      if (handled) {
        return next(action);
      }
      notification = notificationMessages.auth.SESSION_EXPIRED;
    } else if (status >= 400 && status < 500) {
      const customMessage = data?.message || data?.error;
      notification = {
        type: 'error',
        title: 'Request Failed',
        message: customMessage || 'Please check your input and try again',
        priority: 'normal',
        dismissible: true,
        duration: 5000,
      };
    } else {
      notification = notificationMessages.system.ERROR;
    }

    api.dispatch(addNotification(notification));
  }

  return next(action);
};
